// DraftItemEditRow — one editable cargo-position card inside the
// optimistic draft scratchpad. It keeps local photo preview state so
// the user sees the selected image immediately, before the presigned
// upload finishes.
//
// @aiPurpose Render and route edits for one DraftItem.
// @aiInvariant `frozen=true` makes every editable affordance inert
//   (inputs `readOnly`, photo / delete IconButtons `disabled`,
//   upload Button `disabled`). The freeze must NEVER produce a
//   wire-side draft-update — DraftEditor's persistence model is
//   create-then-transition, not PATCH-on-edit.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Icon, IconButton, Input, Textarea } from '../../../ui/index.js';
import type { DraftItem, RequestKind } from '../types.js';
import { totalsFromDraftRows } from '../packageCalc.js';
import { DimensionsBlock, makeDraftPackageRow } from './DraftItemDimensions.js';
import { Field, Grid, parseQuantityInput } from './DraftFormPrimitives.js';
import { formatDraftDecimal } from '../draft-media/decimal.js';
import { useDecimalTextInput } from './useDecimalTextInput.js';
import {
  extractFromClipboardApi,
  extractFromClipboardEvent,
  type ClipboardExtractError,
} from '../clipboardExtract.js';
import { DraftPhotoUploadControls } from './DraftPhotoUploadControls.js';
import { DraftItemPhotos } from './DraftItemPhotos.js';
import { useDraftPhotoPreviews } from './DraftPhotoPreviews.js';

// WHY: mirrors the gate used by `ItemsTab.ItemPhotoCell` — we only
//   POST `/photo-download-url` for keys whose shape matches the
//   issued S3 layout `request-item-photo/<userUuid>/<objectUuid>`.
//   Local-only blob previews and pre-save object keys never call the
//   endpoint. Backend additionally validates the prefix; this local
//   gate prevents the round-trip in the first place.
const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

function isPersistedItemId(id: string): boolean {
  return /^it-\d{4}$/.test(id);
}

export interface DraftItemEditRowProps {
  item: DraftItem;
  uploading: boolean;
  uploadError: string | null;
  frozen: boolean;
  // WHY: server request id pinned after the draft's first save.
  //   Combined with a `it-NNNN` item id and a validly shaped stored
  //   object key, it gates the signed download-url fetch that
  //   restores the thumbnail after the row remounts on id remap.
  persistedRequestId: string | null;
  // WHY (#1056): the parent draft's kind discriminator. CONTAINER
  //   hides the places-count input — the parent container itself is
  //   the place — while keeping the productQuantity field visible.
  //   Optional with default `'STANDARD'` so existing test fixtures
  //   keep working. SIMPLE is non-container, so it follows the STANDARD
  //   layout via the `=== 'CONTAINER'` discriminator below.
  kind?: RequestKind;
  onPatch: (p: Partial<Omit<DraftItem, 'id'>>) => void;
  onUploadPhoto: (file: File) => Promise<void>;
  onClearPhoto: () => void;
  // WHY (#1451): SIMPLE multi-photo handlers. Only read when
  //   `kind === 'SIMPLE'`; the single-photo upload/clear props above
  //   drive every other kind unchanged.
  onAddPhoto?: (file: File) => Promise<{ objectKey: string } | null>;
  onRemovePhoto?: (key: string) => void;
  // WHY (#1441): clone this item via the card-header duplicate affordance.
  onDuplicate: () => void;
  onRemove: () => void;
  // WHY (#1439): set to true only for the row that was just added so its
  //   name input receives focus on mount. autoFocus fires once on
  //   mount and never re-fires; already-mounted rows are unaffected.
  autoFocusName?: boolean;
}

export function DraftItemEditRow({
  item,
  uploading,
  uploadError,
  frozen,
  persistedRequestId,
  kind = 'STANDARD',
  onPatch,
  onUploadPhoto,
  onClearPhoto,
  onAddPhoto,
  onRemovePhoto,
  onDuplicate,
  onRemove,
  autoFocusName,
}: DraftItemEditRowProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Session blob previews live in the draft-scoped registry, keyed by
  // object key, so a cloned position reads this row's preview by key (#1493).
  const { getPreviewUrl, registerBlob, registerAuthorizer, requestKey } = useDraftPhotoPreviews();
  // WHY (#1437): the direct weight/volume fields own a local text buffer
  //   via the shared hook so an in-progress fractional value is not wiped
  //   by the parent patch+rerender. In dimensions mode the displays below
  //   bypass the buffer and show the computed auto-total instead.
  const weightField = useDecimalTextInput(item.weight, (weight) => onPatch({ weight }));
  const volumeField = useDecimalTextInput(item.volume, (volume) => onPatch({ volume }));
  const [quantityText, setQuantityText] = useState(() => String(item.quantity));
  // WHY (#1056): productQuantity has its own controlled text mirror —
  //   `null` (no value yet) shows as an empty input, a number shows as
  //   its string form. Parsing on change re-emits null when the input
  //   is blanked so the wire round-trip preserves «no value».
  const [productQuantityText, setProductQuantityText] = useState(() =>
    item.productQuantity === null ? '' : String(item.productQuantity),
  );
  const isContainer = kind === 'CONTAINER';
  const dimensionsMode = item.dimensionsMode === true;
  const computedTotals = useMemo(
    () => (dimensionsMode ? totalsFromDraftRows(item.packages) : null),
    [dimensionsMode, item.packages],
  );
  // INVARIANT (#590): when dimensions mode is on, the editor surfaces the
  //   computed totals instead of the manual input strings — backend stays
  //   authoritative on save, but the user needs immediate feedback while
  //   typing a package row.
  const weightDisplay = dimensionsMode
    ? computedTotals
      ? formatDraftDecimal(computedTotals.weight)
      : ''
    : weightField.text;
  const volumeDisplay = dimensionsMode
    ? computedTotals
      ? formatDraftDecimal(computedTotals.volume)
      : ''
    : volumeField.text;
  const quantityDisplay = dimensionsMode
    ? computedTotals
      ? String(computedTotals.quantity)
      : ''
    : quantityText;
  // INVARIANT: clipboard errors live in their own slot so an upload
  // failure does not clobber a clipboard error and vice versa. Pending
  // file is the still-uncommitted clipboard image waiting on the
  // replacement confirmation — it must NOT increment
  // `pendingPhotoUploads` (close/save/send guards see it as "no
  // upload in flight" because the network call has not started).
  const [clipboardError, setClipboardError] = useState<ClipboardExtractError | null>(null);
  const [pendingClipboardFile, setPendingClipboardFile] = useState<File | null>(null);
  const hasStoredPhoto = item.photos.length > 0;
  const canRetry = selectedFile !== null && uploadError !== null && !uploading && !frozen;
  const photoActionsDisabled = uploading || frozen;
  const storedKey = item.photos[0] ?? null;
  // Resolve a saved single-photo's signed-download URL through the
  // registry (#1494): a persisted, non-SIMPLE row authorizes the key and
  // asks the registry to fetch it, so a draft-it-N clone reads the cached
  // URL by key. SIMPLE rows render DraftItemPhotos, which does its own.
  useEffect(() => {
    if (
      kind === 'SIMPLE' ||
      storedKey === null ||
      persistedRequestId === null ||
      !isPersistedItemId(item.id) ||
      !ISSUED_PHOTO_KEY_RE.test(storedKey)
    ) {
      return;
    }
    registerAuthorizer(storedKey, persistedRequestId, item.id);
    requestKey(storedKey);
  }, [kind, storedKey, persistedRequestId, item.id, registerAuthorizer, requestKey]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // First-photo key present when the user picked a file. Lets the
  // register effect below tell a resolved upload (storedKey moved to the
  // new file's key) from one still in flight (storedKey is still the old
  // key). Without this, replacing an existing photo binds the new file to
  // the OLD key before the upload resolves, so a clone renders the new
  // image under the old key it actually references (#1493).
  const keyAtSelectRef = useRef<string | null>(null);

  // Register the session blob ONLY under the key THIS upload produced.
  // selectFile sets selectedFile before the upload resolves, so during a
  // replacement storedKey is still the previous key; binding the new file
  // to it would mismatch a clone (and a failed upload would persist that
  // mismatch). Register only once storedKey differs from the key captured
  // at selection time. registerBlob is idempotent.
  useEffect(() => {
    if (selectedFile !== null && storedKey !== null && storedKey !== keyAtSelectRef.current) {
      registerBlob(storedKey, selectedFile);
    }
  }, [storedKey, selectedFile, registerBlob]);

  async function selectFile(file: File): Promise<void> {
    keyAtSelectRef.current = item.photos[0] ?? null;
    setClipboardError(null);
    setSelectedFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return createObjectUrl(file);
    });
    await onUploadPhoto(file);
  }

  function clearPhoto(): void {
    setClipboardError(null);
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onClearPhoto();
  }

  // Route both the explicit-button click and the focused-button paste
  // through one place so the replacement-confirmation flow stays the
  // single decision point. `uploading || frozen` short-circuits here
  // even though the button itself is disabled — paste fires through
  // the DOM listener regardless of disabled style.
  function attachClipboardFile(file: File): void {
    if (photoActionsDisabled) return;
    setClipboardError(null);
    if (hasStoredPhoto || previewUrl) {
      setPendingClipboardFile(file);
      return;
    }
    void selectFile(file);
  }

  async function onClipboardButtonClick(): Promise<void> {
    if (photoActionsDisabled) return;
    setClipboardError(null);
    const result = await extractFromClipboardApi();
    if (!result.ok) {
      setClipboardError(result.error);
      return;
    }
    attachClipboardFile(result.file);
  }

  function onClipboardPaste(e: ReactClipboardEvent<HTMLElement>): void {
    if (photoActionsDisabled) return;
    const result = extractFromClipboardEvent(e.clipboardData);
    if (!result.ok) {
      // No accepted image in clipboard or unsupported type. Do not
      // call preventDefault — the browser's default no-op is fine and
      // calling preventDefault on a non-handled paste would suppress
      // any other passive listeners.
      setClipboardError(result.error);
      return;
    }
    e.preventDefault();
    attachClipboardFile(result.file);
  }

  return (
    <div
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        padding: 12,
        display: 'grid',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="package" size={16} style={{ color: 'var(--fg-3)' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>
          {item.name.trim() || t('requests:draft.newItemName')}
        </div>
        <div style={{ flex: 1 }} />
        <Button kind="quiet" size="sm" icon="copy" onClick={onDuplicate} disabled={frozen}>
          {t('requests:draft.duplicateItem')}
        </Button>
        <IconButton
          icon="trash-2"
          size={24}
          label={t('common:actions.delete')}
          onClick={onRemove}
          disabled={frozen}
        />
      </div>

      <Grid minWidth={220}>
        <Field label={t('requests:draft.table.name')} required>
          <Input
            value={item.name}
            onChange={(v) => onPatch({ name: v })}
            placeholder={t('requests:draft.itemNamePlaceholder')}
            readOnly={frozen}
            ariaLabel={t('requests:draft.table.name')}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocusName === true}
          />
        </Field>
        <Field label={t('requests:draft.table.brand')}>
          <Input
            value={item.brand}
            onChange={(v) => onPatch({ brand: v })}
            placeholder={t('common:fields.optional')}
            readOnly={frozen}
            ariaLabel={t('requests:draft.table.brand')}
          />
        </Field>
        <Field label={t('requests:draft.table.material')}>
          <Input
            value={item.material}
            onChange={(v) => onPatch({ material: v })}
            placeholder={t('common:fields.optional')}
            readOnly={frozen}
            ariaLabel={t('requests:draft.table.material')}
          />
        </Field>
      </Grid>

      <Field label={t('requests:draft.table.description')} required>
        <Textarea
          rows={3}
          value={item.description}
          onChange={(v) => onPatch({ description: v })}
          placeholder={t('requests:draft.itemDescriptionPlaceholder')}
          readOnly={frozen}
          ariaLabel={t('requests:draft.table.description')}
          style={{ resize: frozen ? 'none' : 'vertical' }}
        />
      </Field>

      <Grid minWidth={120}>
        <Field label={t('requests:draft.table.weight')} required>
          <Input
            monospace
            value={weightDisplay}
            onChange={(v) => {
              if (dimensionsMode) return;
              weightField.onChange(v);
            }}
            {...(!frozen && !dimensionsMode && { onBlur: weightField.onBlur })}
            placeholder="0"
            readOnly={frozen || dimensionsMode}
            ariaLabel={t('requests:draft.table.weight')}
          />
        </Field>
        <Field label={t('requests:draft.table.volume')} required>
          <Input
            monospace
            value={volumeDisplay}
            onChange={(v) => {
              if (dimensionsMode) return;
              volumeField.onChange(v);
            }}
            {...(!frozen && !dimensionsMode && { onBlur: volumeField.onBlur })}
            placeholder="0"
            readOnly={frozen || dimensionsMode}
            ariaLabel={t('requests:draft.table.volume')}
          />
        </Field>
        {/* WHY (#1056): CONTAINER drafts hide the places control —
            the parent container is the place — but keep productQuantity
            below. STANDARD drafts surface both with explicit labels. */}
        {!isContainer && (
          <Field label={t('requests:draft.table.placesCount')} required>
            <Input
              monospace
              value={quantityDisplay}
              onChange={(v) => {
                if (dimensionsMode) return;
                setQuantityText(v);
                onPatch({ quantity: parseQuantityInput(v) });
              }}
              placeholder="1"
              readOnly={frozen || dimensionsMode}
              ariaLabel={t('requests:draft.table.placesCount')}
            />
          </Field>
        )}
        <Field label={t('requests:draft.table.productQuantity')}>
          <Input
            monospace
            value={productQuantityText}
            onChange={(v) => {
              setProductQuantityText(v);
              const trimmed = v.trim();
              if (trimmed.length === 0) {
                onPatch({ productQuantity: null });
                return;
              }
              const parsed = parseQuantityInput(trimmed);
              if (Number.isFinite(parsed) && parsed > 0) {
                onPatch({ productQuantity: parsed });
              }
            }}
            placeholder={t('common:fields.optional')}
            readOnly={frozen}
            ariaLabel={t('requests:draft.table.productQuantity')}
          />
        </Field>
      </Grid>
      <DimensionsBlock
        item={item}
        frozen={frozen}
        dimensionsMode={dimensionsMode}
        onToggle={(next) => {
          // WHY (#590): turning the toggle ON without any rows seeds one
          //   empty row so the user has something to type into. Turning
          //   it OFF keeps the rows in store so a re-toggle restores them.
          const patch: Partial<Omit<DraftItem, 'id'>> = { dimensionsMode: next };
          if (next && item.packages.length === 0) {
            patch.packages = [makeDraftPackageRow()];
          }
          if (next) {
            // Sync the manual mirror so canonicalize sees consistent state
            // for the manual inputs while dimensions mode is on; backend
            // recomputes from packages on save, but the local view shows
            // the totals so we keep weight/volume in step.
            const totals = totalsFromDraftRows(patch.packages ?? item.packages);
            patch.weight = totals.weight;
            patch.volume = totals.volume;
            patch.quantity = totals.quantity;
          }
          onPatch(patch);
        }}
        onPackagesChange={(rows) => {
          const totals = totalsFromDraftRows(rows);
          onPatch({
            packages: rows,
            weight: totals.weight,
            volume: totals.volume,
            quantity: totals.quantity,
          });
        }}
      />

      <Field label={t('requests:draft.table.photo')} required>
        {kind === 'SIMPLE' ? (
          // WHY (#1451): SIMPLE positions get the multi-photo strip +
          //   gallery. The strip + gallery trigger live in
          //   DraftItemPhotos so this row stays under the file-size cap.
          <DraftItemPhotos
            itemId={item.id}
            photos={item.photos}
            persistedRequestId={persistedRequestId}
            uploading={uploading}
            uploadError={uploadError}
            frozen={frozen}
            onAddPhoto={(file) => onAddPhoto?.(file) ?? Promise.resolve(null)}
            onRemovePhoto={(key) => onRemovePhoto?.(key)}
          />
        ) : (
          <DraftPhotoUploadControls
            preview={
              <PhotoPreview
                previewUrl={previewUrl}
                storedUrl={storedKey !== null ? getPreviewUrl(storedKey) : null}
                hasStoredPhoto={hasStoredPhoto}
              />
            }
            hasStoredPhoto={hasStoredPhoto}
            hasLocalPreview={previewUrl !== null}
            uploading={uploading}
            uploadError={uploadError}
            clipboardError={clipboardError}
            photoActionsDisabled={photoActionsDisabled}
            clearDisabled={frozen}
            onSelectFile={(file) => {
              void selectFile(file);
            }}
            onClear={clearPhoto}
            onClipboardButtonClick={() => {
              void onClipboardButtonClick();
            }}
            onClipboardPaste={onClipboardPaste}
            canRetry={canRetry}
            onRetry={() => {
              if (selectedFile) void onUploadPhoto(selectedFile);
            }}
          />
        )}
      </Field>
      {pendingClipboardFile && (
        <ConfirmModal
          title={t('requests:draft.photoReplaceConfirm.title')}
          text={t('requests:draft.photoReplaceConfirm.body')}
          confirmLabel={t('requests:draft.photoReplaceConfirm.confirm')}
          cancelLabel={t('requests:draft.photoReplaceConfirm.cancel')}
          onConfirm={() => {
            const file = pendingClipboardFile;
            setPendingClipboardFile(null);
            void selectFile(file);
          }}
          onClose={() => {
            setPendingClipboardFile(null);
          }}
        />
      )}
    </div>
  );
}

function PhotoPreview({
  previewUrl,
  storedUrl,
  hasStoredPhoto,
}: {
  previewUrl: string | null;
  storedUrl: string | null;
  hasStoredPhoto: boolean;
}) {
  // INVARIANT: this row's own local blob preview always wins — it's the
  //   freshly selected file and reflects the user's intent before the
  //   server round-trip. The registry preview (a session blob OR a saved
  //   signed-download URL, resolved by key) is the post-save fallback. All
  //   surfaces share the 72x54 box so the layout doesn't jump.
  const displayUrl = previewUrl ?? storedUrl;
  if (displayUrl) {
    return (
      <img
        src={displayUrl}
        alt=""
        style={{
          width: 72,
          height: 54,
          objectFit: 'cover',
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-2)',
          background: 'var(--bg-2)',
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: 72,
        height: 54,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--border-2)',
        background: 'var(--bg-2)',
        color: hasStoredPhoto ? 'var(--status-success-fg)' : 'var(--fg-4)',
      }}
    >
      <Icon name={hasStoredPhoto ? 'image' : 'image-plus'} size={18} />
    </span>
  );
}

function createObjectUrl(file: File): string | null {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : null;
}
